/**
 * Types for the 360° gaze heatmap feature
 */

/** Unity direction vector (x, y, z) - normalized */
export interface Direction3D {
  x: number;
  y: number;
  z: number;
}

/** UV coordinates for equirectangular projection (0-1 range) */
export interface UVCoordinates {
  u: number; // 0 = left edge, 1 = right edge
  v: number; // 0 = top (north pole), 1 = bottom (south pole)
}

/** UV coordinates with weight for fade-out effect in video heatmaps */
export interface WeightedUVCoordinates extends UVCoordinates {
  weight: number; // 0-1, where 1 = full intensity, 0 = faded out
}

/** A single gaze point from player_transform data */
export interface GazePoint {
  time: number;
  direction: Direction3D;
}

/** Gaze point with computed UV coordinates */
export interface GazePointWithUV extends GazePoint {
  uv: UVCoordinates;
}

/** Gaze point with session metadata (for aggregated views) */
export interface AggregatedGazePoint extends GazePoint {
  sessionId: number;
  userId?: number | null;
  username?: string | null;
}

/** Scene type enum */
export type SceneType = "image360" | "video360" | "model3d" | string;

/** 360° media information from source_medias */
export interface SceneMedia {
  id: number;
  url: string;
  urlPreset1?: string | null; // 2K version (ffmpeg preset 1)
  urlPreset2?: string | null; // 4K version (ffmpeg preset 2)
  media360Tag: string | null;
  type: "image" | "video" | string;
  sceneType: SceneType | null; // Scene type from content_scene (image360, video360, etc.)
  duration: number | null; // For videos, in seconds (full video duration)
  rotationY?: number; // Scene rotation in degrees (Y axis) for coordinate conversion
  timeBegin?: number; // Video crop start time in seconds (from content_scene)
  timeEnd?: number | null; // Video crop end time in seconds (from content_scene)
}

/** Scene data from gazeHeatmapData API */
export interface SceneGazeData {
  sceneId: number;
  sceneName: string;
  timeStart: number;
  timeEnd: number;
  duration: number;
  pointCount: number;
  points: GazePoint[];
  media: SceneMedia | null;
}

/** Response from /scores/gazeHeatmapData API */
export interface GazeHeatmapDataResponse {
  sessionId: number;
  scenes: SceneGazeData[];
  totalPointCount: number;
}

/** Session info in aggregated view */
export interface SessionGazeInfo {
  sessionId: number;
  userId?: number | null;
  username?: string | null;
  pointCount: number;
}

/** Response from /scores/sceneGazeHeatmapData API */
export interface SceneGazeHeatmapDataResponse {
  sceneName: string;
  sceneId: number;
  sceneMedia: SceneMedia | null;
  sessions: SessionGazeInfo[];
  points: AggregatedGazePoint[];
  totalPointCount: number;
  sessionCount: number;
}

/** Heatmap color schemes */
export type HeatmapColorScheme = "green" | "thermal" | "viridis";

/** View mode for heatmap display */
export type HeatmapViewMode = "flat" | "360";

/** Configuration for heatmap generation */
export interface HeatmapGenerationConfig {
  /** Canvas width in pixels (default: 2048) */
  width: number;
  /** Canvas height in pixels (default: 1024) */
  height: number;
  /** Gaussian kernel radius in pixels (default: 30) */
  kernelRadius: number;
  /** Gaussian kernel sigma (default: 15) */
  kernelSigma: number;
  /** Color scheme to use */
  colorScheme: HeatmapColorScheme;
  /** Maximum density value for normalization (auto-calculated if not provided) */
  maxDensity?: number;
}

/** Result from heatmap generation */
export interface HeatmapGenerationResult {
  /** The generated heatmap as ImageData */
  imageData: ImageData;
  /** Maximum density value found */
  maxDensity: number;
  /** Number of points processed */
  pointCount: number;
  /** Time taken in milliseconds */
  processingTime: number;
}

/** Props for the main GazeHeatmap360Viewer component */
export interface GazeHeatmap360ViewerProps {
  /** Session ID for individual session view */
  sessionId?: number;
  /** Content ID (scene) - required for aggregated view */
  contentId?: number;
  /** Session IDs for aggregated view */
  sessionIds?: number[];
  /** Group member IDs for filtering (user-X or number format) */
  groupMemberIds?: (number | string)[];
  /** Duration of each video segment in seconds (default: 10) */
  segmentDuration?: number;
  /** Heatmap overlay opacity (0-1, default: 0.6) */
  heatmapOpacity?: number;
  /** Color scheme for heatmap (default: "green") */
  colorScheme?: HeatmapColorScheme;
  /** Callback when segment changes */
  onSegmentChange?: (segment: number) => void;
  /** Callback when data loads */
  onDataLoad?: (pointCount: number) => void;
}

/** Video segment for timeline navigation */
export interface VideoSegment {
  index: number;
  startTime: number;
  endTime: number;
  pointCount: number;
}

/** State for the heatmap viewer */
export interface HeatmapViewerState {
  /** Current view mode */
  viewMode: "flat" | "360";
  /** Current segment index (for videos) */
  currentSegment: number;
  /** Total number of segments */
  totalSegments: number;
  /** Whether segments are synced in comparison view */
  syncSegments: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
}

/** Props for comparison view */
export interface HeatmapComparisonViewProps {
  /** Scene content ID */
  contentId: number;
  /** Groups to compare (up to 3) */
  groups: {
    id: string;
    name: string;
    memberIds: (number | string)[];
    color?: string;
  }[];
  /** Segment duration in seconds */
  segmentDuration?: number;
  /** Whether to sync segment navigation across groups */
  syncSegments?: boolean;
  /** Color scheme */
  colorScheme?: HeatmapColorScheme;
  /** Close callback */
  onClose?: () => void;
}

/** Message sent to heatmap Web Worker */
export interface HeatmapWorkerMessage {
  type: "generate";
  points: UVCoordinates[];
  config: HeatmapGenerationConfig;
}

/** Message for batch generating video heatmap keyframes */
export interface HeatmapBatchWorkerMessage {
  type: "generate-batch";
  keyframes: {
    timeCenter: number;
    points: WeightedUVCoordinates[];
  }[];
  config: HeatmapGenerationConfig;
}

/** Message received from heatmap Web Worker */
export interface HeatmapWorkerResponse {
  type: "result" | "error" | "progress";
  result?: HeatmapGenerationResult;
  error?: string;
  progress?: number;
}

/** Response for batch keyframe generation */
export interface HeatmapBatchWorkerResponse {
  type: "batch-result" | "batch-progress" | "error";
  results?: PrecomputedHeatmapFrame[];
  progress?: number; // 0-100
  currentKeyframe?: number;
  totalKeyframes?: number;
  error?: string;
}

/** Configuration for video heatmap sliding window */
export interface VideoHeatmapConfig {
  /** Duration of the sliding window in seconds (default: 3s) */
  windowDuration: number;
  /** Ratio of window that fades out (default: 0.6 = 60% of window) */
  fadeOutRatio: number;
  /** Interval between keyframes in seconds (default: 0.5s) */
  keyframeInterval: number;
}

/** A pre-computed heatmap frame for video playback */
export interface PrecomputedHeatmapFrame {
  /** Time center of this keyframe in seconds */
  timeCenter: number;
  /** The pre-computed heatmap ImageData as transferable array */
  imageDataArray: Uint8ClampedArray;
  /** Image width */
  width: number;
  /** Image height */
  height: number;
}

/** Cache of pre-computed keyframes for video heatmap */
export interface HeatmapCache {
  /** Array of pre-computed frames */
  frames: PrecomputedHeatmapFrame[];
  /** Video duration in seconds */
  videoDuration: number;
  /** Keyframe interval used */
  keyframeInterval: number;
  /** Whether precomputation is complete */
  isComplete: boolean;
}

// ============================================================================
// Scanpath / Trajectory Types
// ============================================================================

/**
 * A fixation point detected via spatial clustering
 */
export interface Fixation {
  /** Unique ID for this fixation */
  id: number;
  /** Start time in seconds */
  startTime: number;
  /** End time in seconds */
  endTime: number;
  /** Duration in milliseconds */
  duration: number;
  /** Centroid position in UV coordinates */
  centroidUV: UVCoordinates;
  /** Number of gaze points in this fixation cluster */
  pointCount: number;
  /** Original 3D direction at centroid */
  centroidDirection: Direction3D;
  /** Strength score (0-1) based on point count relative to max in session */
  strength: number;
}

/**
 * A saccade (rapid eye movement between fixations)
 */
export interface Saccade {
  /** Start fixation ID */
  fromFixationId: number;
  /** End fixation ID */
  toFixationId: number;
  /** Duration in milliseconds */
  duration: number;
  /** Angular amplitude in degrees */
  amplitude: number;
  /** Start UV position */
  startUV: UVCoordinates;
  /** End UV position */
  endUV: UVCoordinates;
}

/**
 * Complete scanpath data for a session or segment
 */
export interface ScanpathData {
  /** Detected fixations in temporal order */
  fixations: Fixation[];
  /** Saccades between consecutive fixations */
  saccades: Saccade[];
  /** Total duration of all fixations / total duration (0-1) */
  fixationRatio: number;
  /** Total number of fixations */
  fixationCount: number;
  /** Average fixation duration in milliseconds */
  averageFixationDuration: number;
  /** Average saccade amplitude in degrees */
  averageSaccadeAmplitude: number;
  /** Processing time in milliseconds */
  processingTime: number;
}

/**
 * Configuration for fixation detection and display
 *
 * Two controls:
 * 1. Sensitivity (displayThreshold): filters which fixations are shown by strength
 *    - High sensitivity = show all fixations including weak ones
 *    - Low sensitivity = show only strong fixations
 *
 * 2. Merge radius: controls how nearby cells are merged into single fixations
 *    - 0 = no merging, maximum granularity
 *    - Higher = more aggressive merging, fewer but larger fixations
 */
export interface FixationDetectionConfig {
  /**
   * Display threshold (0-1): minimum strength to display a fixation
   * 0 = show all fixations, 1 = show only the strongest
   * Default: 0 (show all)
   */
  displayThreshold?: number;
  /**
   * Merge radius (0-4): how far apart cells can be merged
   * 0 = no merging (each cell is its own fixation)
   * 1 = merge adjacent cells only
   * 2-4 = merge cells within increasing distance
   * Default: 1 (adjacent merging)
   */
  mergeRadius?: number;
  /** @deprecated Use displayThreshold instead */
  minPointPercentage?: number;
  /** @deprecated Use mergeRadius instead */
  minAbsolutePoints?: number;
}

/**
 * Default configuration for fixation detection
 */
export const DEFAULT_FIXATION_CONFIG: FixationDetectionConfig = {
  displayThreshold: 0, // Show all fixations by default
  mergeRadius: 1, // Merge adjacent cells
};

// ============================================================================
// Differential Heatmap Types
// ============================================================================

/**
 * Color schemes for differential heatmaps (divergent palettes)
 */
export type DifferentialColorScheme = "redBlue" | "redGray" | "purpleGreen";

/**
 * Normalization method for differential heatmaps
 */
export type DifferentialNormalizationMethod = "perSample" | "maxDensity" | "zscore";

/**
 * Configuration for differential heatmap generation
 */
export interface DifferentialHeatmapConfig {
  /** Canvas width in pixels (default: 2048) */
  width: number;
  /** Canvas height in pixels (default: 1024) */
  height: number;
  /** Gaussian kernel radius in pixels (default: 30) */
  kernelRadius: number;
  /** Gaussian kernel sigma (default: 15) */
  kernelSigma: number;
  /** Color scheme for differential visualization */
  colorScheme: DifferentialColorScheme;
  /** Normalization method */
  normalization: DifferentialNormalizationMethod;
  /** Contrast factor for the difference (default: 1.0) */
  contrastFactor: number;
}

/**
 * Default configuration for differential heatmap
 */
export const DEFAULT_DIFFERENTIAL_CONFIG: DifferentialHeatmapConfig = {
  width: 2048,
  height: 1024,
  kernelRadius: 30,
  kernelSigma: 15,
  colorScheme: "redBlue",
  normalization: "perSample",
  contrastFactor: 1.0,
};

/**
 * Result from differential heatmap generation
 */
export interface DifferentialHeatmapResult {
  /** The generated differential heatmap as ImageData */
  imageData: ImageData;
  /** Maximum positive difference (Group A > Group B) */
  maxPositive: number;
  /** Maximum negative difference (Group B > Group A) */
  maxNegative: number;
  /** Statistics for Group A */
  groupAStats: {
    pointCount: number;
    maxDensity: number;
    meanDensity: number;
  };
  /** Statistics for Group B */
  groupBStats: {
    pointCount: number;
    maxDensity: number;
    meanDensity: number;
  };
  /** Processing time in milliseconds */
  processingTime: number;
}

// ============================================================================
// Visualization Mode Types
// ============================================================================

/**
 * Visualization overlay mode for the heatmap viewer
 */
export type VisualizationMode = "heatmap" | "scanpath" | "both";

/**
 * Comparison view mode
 */
export type ComparisonViewMode = "sideBySide" | "differential";
